// ── Beyond the Dashboard ─────────────────────────────────────────────────────
// Concept case study: an intelligent, connected truck HMI experience, told from
// the point of view of India's long-haul commercial drivers. Framed carefully as
// a PROPOSAL — desk research + concept design, with validation and
// implementation explicitly called out as future phases.

const IMG = {
    hero: 'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/aee3e3f06a641eff411acc39ea06e463fdf9ecfcf510cb553e3dea06e6982610.jpeg',
    journeyCommander:
        'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/69f3eae3400670d2ada8ce38dcba213dc24739635e0bafaaae212ae5c638c4fc.jpeg',
    checkpointCopilot:
        'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/70ebb174ef62d39df73d0c956d980ff216e7dbf8c4436fcb3de9c7d1551f2f51.jpeg',
    roadWellbeing:
        'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/b3cb397e28588add23c7e778a4714121933f6907d1e034d972a59041b5861d0c.jpeg',
    modeDriving:
        'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/c6e4132c8dedf471c9e0ce129d573c461068d68ea3eb1deee3d78b02bbc2c202.jpeg',
    modeIdle:
        'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/05c96f63ff4bb28535484d3d88d2fa4570ef9b0cc60b92c13ab8b734cbcc05e3.jpeg',
    modeRest:
        'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/7cf6dae816f27c97e3853193594e89dfe9a619d2097d350d1732d906abacfd67.jpeg',
    finalShowcase:
        'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/4061c7ba7610cce35cec81148afc4480921f6c6d368bbca810e43e40c97d1c93.jpeg',
    sbStart:
        'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/0731a0bb9f219ce9fdce09503f02b8998949f584513a51f0a18f298f5da8b68b.jpeg',
    sbNavigate:
        'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/8c616d019f276621a4f7ce54975cc6fe0c52b83fb22602236c7af4c38acd5ad0.jpeg',
    sbCheckpoint:
        'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/50f2de296f730654057968c69457d7719d93b9c4889a96f465a4920ac22e7103.jpeg',
    sbFatigue:
        'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/87115905c07d2f412f946b2d84c803fb2984df4a7760975992ed66817bdc4f97.jpeg',
    sbDeliver:
        'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/fd3a4d6d69c45d75735cf5666b07b418c40fd3b22b3488bc1eeb9378117cb975.jpeg',
    sbRewards:
        'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/c9b7748909c2f802ab91a22af16daf9e3b2d33e7a5b632062d8e5af5d166af59.jpeg',
};

export const beyondTheDashboard = {
    slug: 'beyond-the-dashboard',
    no: '09',
    type: 'rich',
    title: 'The Future of Intelligent Trucks',
    subtitle: 'From connected vehicles to intelligent journeys',
    conceptRibbon: true,
    category: 'Concept · HMI',
    cover: IMG.hero,
    client: 'Concept project · Indian Commercial Vehicles · Connected Mobility',
    timeline: 'Concept Design · UX lead',
    responsibilities: [
        'UX strategy',
        'Journey mapping',
        'Experience concepts',
        'Interaction flows',
        'HMI visual design',
    ],
    overview:
        'This concept explores how connected truck technologies can create a more intelligent, driver-centred experience for India\u2019s long-haul commercial drivers. It examines opportunities across the driver\u2019s journey on the National Highways \u2014 from FASTag tolls and interstate checkpoints to dhaba rest stops \u2014 and proposes a contextual human\u2013machine interface (HMI) that brings vehicle information, navigation, fleet coordination and connected services together, making relevant information easier to access and decisions easier to navigate. A proposed experience; validation and implementation are future phases.',
    blocks: [
        {
            kind: 'tabs',
            tabs: [
                // ═══════════════════════════════════════════════════════
                // 01 · THE CHALLENGE
                // ═══════════════════════════════════════════════════════
                {
                    label: 'The Challenge',
                    hero: IMG.sbStart,
                    summary:
                        'Why India needs an in-cab experience that finally catches up with the connected technology already in the truck.',
                    highlights: [
                        { icon: 'navigation', title: 'Fragmented today', body: 'Tolls, documents, navigation and fleet sit across separate apps and paper.' },
                        { icon: 'gauge', title: 'Connected cabins', body: 'OEMs are adding telematics, so the in-cab experience must catch up.' },
                        { icon: 'user', title: 'Driver-first', body: 'Grounded in the everyday reality of long-haul drivers in India.' },
                    ],
                    blocks: [
                        {
                            kind: 'meta-list',
                            items: [
                                { label: 'Project type', value: 'Concept design' },
                                { label: 'Domain', value: 'Indian Commercial Vehicles \u00b7 Connected Mobility' },
                                { label: 'Point of view', value: 'India\u2019s long-haul truck drivers on the National Highways' },
                                { label: 'Disciplines', value: 'UX strategy \u00b7 Journey mapping \u00b7 Experience design \u00b7 Interaction design \u00b7 HMI concepts' },
                                { label: 'Status', value: 'Proposed experience \u2014 validation and implementation are future phases' },
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'India\u2019s trucks are getting connected. The experience needs to catch up',
                            paragraph:
                                'Commercial vehicles in India now run within a digital ecosystem \u2014 GPS navigation, FASTag tolling, the e-way bill, telematics, interstate permits, fleet coordination and roadside services. But these stay fragmented across separate apps, paper documents and the driver\u2019s own phone \u2014 so a long-haul driver juggles toll balances, checkpoint documents, transporter calls and navigation, all while managing the highway.',
                        },
                        {
                            kind: 'grid-cards',
                            items: [
                                {
                                    title: 'The design challenge',
                                    body: 'How might we bring vehicle intelligence, journey context and India-specific services \u2014 tolls, documents, dhabas, fleet \u2014 together to reduce complexity and support better decisions on the road?',
                                },
                                {
                                    title: 'The opportunity',
                                    body: 'Move from a dashboard that simply presents information to an experience that helps the driver understand what matters next.',
                                },
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'The industry shift',
                            paragraph:
                                'Indian trucks are moving from basic instrument clusters toward connected, software-defined cabins, with OEMs like Tata Motors, Ashok Leyland and BharatBenz adding telematics. This chapter is grounded in desk research and a light review of how trucks present in-cab information \u2014 not primary field studies, which remain a future phase.',
                        },
                    ],
                },
                // ═══════════════════════════════════════════════════════
                // 02 · THE JOURNEY
                // ═══════════════════════════════════════════════════════
                {
                    label: 'The Journey',
                    hero: IMG.sbNavigate,
                    summary:
                        'A day in the life on the Bengaluru to Chennai (NH44) run, six stages, and where the experience breaks down today.',
                    highlights: [
                        { icon: 'compass', title: 'Six stages', body: 'Plan, drive, checkpoint, rest, deliver and grow.' },
                        { icon: 'shield', title: 'Real friction', body: 'Each stage carries its own delays, risks and uncertainty.' },
                        { icon: 'navigation', title: 'One corridor', body: 'Illustrated along the real NH44 route from Bengaluru to Chennai.' },
                    ],
                    blocks: [
                        {
                            kind: 'section',
                            heading: 'A day in the life, Bengaluru to Chennai',
                            paragraph:
                                'A long-haul run moves through distinct stages \u2014 planning the load and e-way bill, driving the highway, clearing tolls and RTO / border checkpoints, resting at a dhaba, and coordinating delivery \u2014 each with its own needs and interruptions. The experience should respond to the driver\u2019s current task, not treat every moment the same. The concept follows one real corridor: Bengaluru \u2013 Hosur \u2013 Krishnagiri \u2013 Chennai (NH44).',
                        },
                        {
                            kind: 'storyboard',
                            heading: 'The journey, stage by stage',
                            scenario:
                                'One delivery, Bengaluru to Chennai (NH44) \u2014 six stages of the run, each showing today\u2019s reality and where it breaks down for the driver. The connected response to each is the matching module in the next chapter.',
                            stages: [
                                {
                                    title: 'Start the trip',
                                    subtitle: 'Plan & prepare',
                                    image: IMG.sbStart,
                                    moduleLabel: 'Trip Readiness',
                                    groups: [
                                        {
                                            label: 'Current experience',
                                            tone: 'current',
                                            items: [
                                                'Receives trip information from multiple systems.',
                                                'Must manually verify permits, invoices and fuel status.',
                                                'Unsure whether the truck is fully ready.',
                                            ],
                                        },
                                        {
                                            label: 'Challenges',
                                            tone: 'challenge',
                                            items: [
                                                'Missing documents discovered later.',
                                                'Vehicle readiness is unclear.',
                                                'Departure decisions based on assumptions.',
                                            ],
                                        },
                                    ],
                                },
                                {
                                    title: 'Navigate the journey',
                                    subtitle: 'Drive efficiently',
                                    image: IMG.sbNavigate,
                                    moduleLabel: 'Journey Commander',
                                    groups: [
                                        {
                                            label: 'Current experience',
                                            tone: 'current',
                                            items: [
                                                'Relies on consumer navigation.',
                                                'Discovers traffic only after reaching congestion.',
                                                'Limited visibility of truck-specific restrictions.',
                                            ],
                                        },
                                        {
                                            label: 'Challenges',
                                            tone: 'challenge',
                                            items: [
                                                'Unexpected delays and wrong routes.',
                                                'Increased fuel consumption.',
                                                'Delivery commitments missed.',
                                            ],
                                        },
                                    ],
                                },
                                {
                                    title: 'Checkpoint & compliance',
                                    subtitle: 'Stay compliant',
                                    image: IMG.sbCheckpoint,
                                    moduleLabel: 'Checkpoint Copilot',
                                    groups: [
                                        {
                                            label: 'Current experience',
                                            tone: 'current',
                                            items: [
                                                'Driver scrambles to find documents.',
                                                'Some documents on phone, some physical copies.',
                                            ],
                                        },
                                        {
                                            label: 'Challenges',
                                            tone: 'challenge',
                                            items: [
                                                'Delays at checkpoints.',
                                                'Compliance uncertainty and challan risks.',
                                                'Stressful inspections.',
                                            ],
                                        },
                                    ],
                                },
                                {
                                    title: 'Fatigue & wellbeing',
                                    subtitle: 'Stay safe',
                                    image: IMG.sbFatigue,
                                    moduleLabel: 'Road & Wellbeing Guardian',
                                    groups: [
                                        {
                                            label: 'Current experience',
                                            tone: 'current',
                                            items: [
                                                'Continues driving to meet the schedule.',
                                                'Limited awareness of nearby rest facilities.',
                                            ],
                                        },
                                        {
                                            label: 'Challenges',
                                            tone: 'challenge',
                                            items: [
                                                'Fatigue and reduced reaction time.',
                                                'Safety risks.',
                                            ],
                                        },
                                    ],
                                },
                                {
                                    title: 'Complete delivery',
                                    subtitle: 'Arrive & close trip',
                                    image: IMG.sbDeliver,
                                    moduleLabel: 'Smart Delivery',
                                    groups: [
                                        {
                                            label: 'Current experience',
                                            tone: 'current',
                                            items: [
                                                'Waits for unloading instructions.',
                                                'Calls warehouse staff.',
                                                'Delivery confirmation is manual.',
                                            ],
                                        },
                                        {
                                            label: 'Challenges',
                                            tone: 'challenge',
                                            items: [
                                                'Waiting time.',
                                                'Manual paperwork.',
                                                'Delayed trip closure.',
                                            ],
                                        },
                                    ],
                                },
                                {
                                    title: 'Grow & earn rewards',
                                    subtitle: 'Beyond the trip',
                                    image: IMG.sbRewards,
                                    moduleLabel: 'Rewards & Growth',
                                    groups: [
                                        {
                                            label: 'Current experience',
                                            tone: 'current',
                                            items: [
                                                'Trip ends with no feedback.',
                                                'No recognition for safe driving.',
                                            ],
                                        },
                                        {
                                            label: 'Challenges',
                                            tone: 'challenge',
                                            items: [
                                                'Low engagement and no driver loyalty.',
                                                'No incentive for efficient behaviour.',
                                            ],
                                        },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                // ═══════════════════════════════════════════════════════
                // 03 · THE PROPOSED EXPERIENCE
                // ═══════════════════════════════════════════════════════
                {
                    label: 'Proposed Experience',
                    hero: IMG.hero,
                    summary:
                        'A connected in-cab experience designed to support drivers throughout the journey with the right information at the right time.',
                    highlights: [
                        { icon: 'navigation', title: 'Context-aware information', body: 'Relevant content based on the current task and location.' },
                        { icon: 'shield', title: 'Proactive assistance', body: 'Alerts for tolls, checkpoints, rest and route changes.' },
                        { icon: 'user', title: 'Simpler interactions', body: 'A driver-friendly UI with clear hierarchy and minimal input.' },
                        { icon: 'link', title: 'Connected ecosystem', body: 'Brings navigation, vehicle data and fleet operations together.' },
                    ],
                    blocks: [
                        {
                            kind: 'section',
                            heading: 'From connected data to contextual decisions',
                            paragraph:
                                'The concept introduces an intelligence layer that connects information from the truck, driver, route, mission, fleet and external conditions \u2014 Indian highway traffic, weather, toll and checkpoint data. Rather than displaying every available data point, the experience aims to prioritise information according to the current situation.',
                        },
                        {
                            kind: 'grid-cards',
                            items: [
                                { title: 'Inputs', body: 'Vehicle + driver + route + mission + Indian road, toll & weather conditions.' },
                                { title: 'Contextual intelligence', body: 'Interprets the situation and what the driver is doing right now.' },
                                { title: 'Relevant surface', body: 'Shows the information and recommended next actions that matter.' },
                                { title: 'Driver decision', body: 'The driver stays in control and makes the final call.' },
                            ],
                        },
                        {
                            kind: 'section',
                            subheading: 'This is a proposed experience model \u2014 not a claim that a production intelligence engine has been implemented.',
                        },
                        {
                            kind: 'section',
                            heading: 'The end-to-end solution',
                            paragraph:
                                'Six connected modules \u2014 one for each stage of the journey \u2014 turn the contextual intelligence into screens the driver actually uses, from trip prep in Bengaluru to rewards after delivery in Chennai.',
                        },
                        {
                            kind: 'sub-tabs',
                            heading: 'Six modules, one journey',
                            tabs: [
                                {
                                    label: 'Trip Readiness',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Trip Readiness',
                                            paragraph:
                                                'Before the wheels roll, an AI readiness score validates documents, checks vehicle health and recommends the best departure time \u2014 so the driver leaves Bengaluru sure the truck and paperwork are good to go.',
                                        },
                                        { kind: 'image', variant: 'hmi', src: 'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/62abb6928f84640f06a362e5cd9db5424a667943579158c9f903e0f405ca782c.jpeg', caption: 'Trip readiness \u2014 documents, vehicle & departure check' },
                                    ],
                                },
                                {
                                    label: 'Journey Commander',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Journey Commander',
                                            paragraph:
                                                'On the road, route progress, upcoming tolls and FASTag balance, stops and ETA come together in one truck-aware view for the Bengaluru \u2192 Chennai run.',
                                        },
                                        { kind: 'image', variant: 'hmi', src: 'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/32d4c6fa5744d37bd352b08cc1a12ee55089f5b48700896fcfe05bf4d8f06a55.jpeg', caption: 'Journey overview \u2014 Bengaluru \u2192 Chennai (NH44)' },
                                    ],
                                },
                                {
                                    label: 'Checkpoint Copilot',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Checkpoint Copilot',
                                            paragraph:
                                                'Approaching the Hosur KA\u2013TN border, the e-way bill, permit, PUC and RC surface automatically \u2014 ready to show, so checkpoints stop being a scramble.',
                                        },
                                        { kind: 'image', variant: 'hmi', src: 'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/6cb85fec7ae3e5176911d471d05b89fefbb4ac21e4567f9ccd35fd86ac40bcde.jpeg', caption: 'Required documents \u2014 Hosur KA\u2013TN border' },
                                    ],
                                },
                                {
                                    label: 'Road & Wellbeing Guardian',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Road & Wellbeing Guardian',
                                            paragraph:
                                                'Through the long middle stretch, fatigue and drive-time are watched gently, with timely rest-stop and dhaba suggestions to keep the journey safe.',
                                        },
                                        { kind: 'image', variant: 'hmi', src: 'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/92ead2dfe735b46abc3cffcc5dbde0168ed02ace6cdb6f6d614058584a0ba6f0.jpeg', caption: 'Wellbeing \u2014 alertness, drive-time & rest guidance' },
                                    ],
                                },
                                {
                                    label: 'Smart Delivery',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Smart Delivery',
                                            paragraph:
                                                'Arriving in Chennai, smart dock assignment, digital proof-of-delivery and automatic trip closure cut the waiting and the paperwork.',
                                        },
                                        { kind: 'image', variant: 'hmi', src: 'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/5b5194c4425470a0bbbf3303314911df10699f4d0c5876ce4678793605f514ba.jpeg', caption: 'Delivery \u2014 dock assignment, digital POD & trip close' },
                                    ],
                                },
                                {
                                    label: 'Rewards & Growth',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Rewards & Growth',
                                            paragraph:
                                                'After the trip, safe-driving scores, fuel-efficiency bonuses, points and rankings recognise good driving and keep drivers engaged beyond a single run.',
                                        },
                                        { kind: 'image', variant: 'hmi', src: 'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/24efd10c67e0cc8f6bde32adb609123e1cb848323a5db98743231b5493b06045.jpeg', caption: 'Rewards \u2014 safe-driving score, points & ranking' },
                                    ],
                                },
                            ],
                        },
                    ],
                },
                // ═══════════════════════════════════════════════════════
                // 04 · DESIGNING THE HMI
                // ═══════════════════════════════════════════════════════
                {
                    label: 'Designing the HMI',
                    hero: IMG.modeDriving,
                    summary:
                        'One system that adapts across driving, idle and rest, with an intent-led interaction model and a calm visual language.',
                    highlights: [
                        { icon: 'gauge', title: 'Adaptive states', body: 'Driving, idle and rest each get the right density and actions.' },
                        { icon: 'sparkles', title: 'Intent-led', body: 'Express a need and get a relevant, contextual response.' },
                        { icon: 'navigation', title: 'Calm visuals', body: 'A low-glare dark cabin surface with a single lime signal colour.' },
                    ],
                    blocks: [
                        {
                            kind: 'section',
                            heading: 'One system. Different driving states',
                            paragraph:
                                'The interface should not behave identically in every situation. Information density, available actions and interaction patterns should reflect whether the truck is moving, parked or in a rest state.',
                        },
                        {
                            kind: 'sub-tabs',
                            heading: 'States of the system',
                            tabs: [
                                {
                                    label: 'Driving mode',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Driving mode',
                                            paragraph:
                                                'Prioritise National Highway navigation, essential vehicle information and relevant alerts such as an upcoming toll plaza. Keep interaction predictable and minimise distraction.',
                                        },
                                        { kind: 'image', variant: 'hmi', src: 'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/2adad821a7c578a7d81621b1340641afbbd94a025da958719ad18850d7aacc94.jpeg', caption: 'Driving mode \u2014 approaching Krishnagiri toll' },
                                    ],
                                },
                                {
                                    label: 'Idle / home mode',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Idle / home mode',
                                            paragraph:
                                                'When parked, provide access to vehicle status, trip preparation, documents, weather, diesel and FASTag, truck parking, dhabas and other useful services.',
                                        },
                                        { kind: 'image', variant: 'hmi', src: 'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/d4720d5220bd737dd7075d5ef199df0ec68b5f939967d87a6588a05779580bb9.jpeg', caption: 'Idle / home mode \u2014 vehicle status at a glance' },
                                    ],
                                },
                                {
                                    label: 'Rest mode',
                                    blocks: [
                                        {
                                            kind: 'section',
                                            heading: 'Rest mode',
                                            paragraph:
                                                'Support the shift from driving to resting with vehicle status, cabin comfort controls, a nearby dhaba and safe parking, rest information and journey resumption.',
                                        },
                                        { kind: 'image', variant: 'hmi', src: 'https://static.prod-images.emergentagent.com/jobs/1dbb8773-a62b-4a3e-b60f-4feacdf5c6e3/images/550c5cd8741f5c6e4f5899448ec0c488b1c237324e53f7c9f79728bd1864da3f.jpeg', caption: 'Rest mode \u2014 rest timer, comfort & nearby dhaba' },
                                    ],
                                },
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'Interaction design: from searching to acting',
                            paragraph:
                                'A menu-driven interface can require drivers to navigate several levels to complete a task. An intent-led concept instead explores expressing a need and receiving a relevant, contextual response.',
                        },
                        {
                            kind: 'grid-cards',
                            items: [
                                {
                                    title: 'Traditional approach',
                                    body: 'Open navigation, search a category, apply filters, review results and pick a dhaba or stop.',
                                },
                                {
                                    title: 'Proposed approach',
                                    body: 'Ask for a safe place to rest and eat, then review nearby dhabas with truck parking based on route, distance and relevant constraints.',
                                },
                            ],
                        },
                        {
                            kind: 'section',
                            subheading: 'The intent-led flow is a design proposal; its usability and feasibility would need to be tested.',
                        },
                        {
                            kind: 'bullets',
                            heading: 'Design principles',
                            columns: 2,
                            items: [
                                'Context before complexity \u2014 prioritise what matters in the current situation.',
                                'Clear visual hierarchy \u2014 make essential information easy to identify.',
                                'Language & literacy \u2014 support Hindi and regional languages with an icon-led, low-text interface.',
                                'Progressive disclosure \u2014 reveal detail when it is relevant.',
                                'Predictable interactions \u2014 maintain consistent navigation and behaviour.',
                                'Driver control \u2014 keep recommendations understandable and decisions in the driver\u2019s hands.',
                                'Safety-led design \u2014 apply motion-appropriate restrictions and validate against safety requirements.',
                            ],
                        },
                    ],
                },
                // ═══════════════════════════════════════════════════════
                // 05 · VALUE, ROADMAP & REFLECTION
                // ═══════════════════════════════════════════════════════
                {
                    label: 'Value & Roadmap',
                    hero: IMG.finalShowcase,
                    summary:
                        'The value a contextual HMI could create across the ecosystem, a phased path to get there, and what comes next.',
                    highlights: [
                        { icon: 'user', title: 'Value for everyone', body: 'For drivers, fleets, transporters and OEMs alike.' },
                        { icon: 'compass', title: 'Phased path', body: 'Deliver value now and expand it as the concept is validated.' },
                        { icon: 'zap', title: 'What comes next', body: 'Validate with drivers and test real highway scenarios.' },
                    ],
                    blocks: [
                        {
                            kind: 'section',
                            heading: 'A more connected experience can create value across the ecosystem',
                            paragraph:
                                'If realised and validated, a contextual HMI could create value for everyone on the Indian freight journey \u2014 described below as intended value propositions, not measured project outcomes.',
                        },
                        {
                            kind: 'grid-cards',
                            items: [
                                { title: 'For drivers', body: 'Easier access to the right information and fewer surprises at tolls, checkpoints and rest stops.' },
                                { title: 'For fleets & transporters', body: 'A possible foundation for better journey coordination and connected operations.' },
                                { title: 'For OEMs', body: 'For Tata, Ashok Leyland and BharatBenz \u2014 a way to connect vehicle capabilities with digital services and future software experiences.' },
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'A phased evolution',
                            paragraph:
                                'The concept is intentionally staged so value can be delivered early and expanded as it is validated.',
                        },
                        {
                            kind: 'grid-cards',
                            items: [
                                { title: 'Now \u2014 foundation', body: 'Unify core information, clarify key workflows and create consistent HMI patterns.' },
                                { title: 'Next \u2014 assistance', body: 'Explore journey-aware recommendations, connected services and more intent-led interactions.' },
                                { title: 'Future \u2014 ecosystem', body: 'Investigate deeper service orchestration and personalisation, subject to technical, safety and user validation.' },
                            ],
                        },
                        {
                            kind: 'section',
                            heading: 'What comes next',
                            paragraph:
                                'The next phase would validate the concept with drivers and fleet / transporter stakeholders, test real highway scenarios, assess distraction and safety constraints, and check technical feasibility with engineering. Likely success measures: task time, interaction steps, error rates, comprehension and perceived workload.',
                        },
                        {
                            kind: 'image',
                            src: IMG.finalShowcase,
                            caption: 'Concept showcase \u2014 driving, parked and rest states',
                        },
                        {
                            kind: 'section',
                            heading: 'Reflection',
                            paragraph:
                                'The central opportunity was not to add more screens or more information. It was to explore how connected capabilities could work together around the Indian driver\u2019s context. A more intelligent truck experience should make the next decision \u2014 the next toll, the next document, the next safe stop \u2014 clearer, not make the driver manage another layer of complexity.',
                        },
                    ],
                },
            ],
        },
    ],
    next: { slug: 'homepage-redesign', title: 'Engagedly Homepage Redesign' },
};
